import { createGlobalStyle } from 'styled-components';

/**
 * Global CSS for the chat brief feature.
 *
 * Ported from the standalone prototype's src/index.css. These rules are
 * scoped to selectors used inside the feature-chat-brief lib (sr-only,
 * typing-dots, typewriter-word, ai-avatar-sparkle-*, chat-input-container,
 * hide-scrollbar, edit-modal-*, modal-sticky-footer, bottom-bar-container).
 *
 * Mount once via `<GlobalChatBriefStyles />` near the root of the feature.
 */
export const GlobalChatBriefStyles = createGlobalStyle`
    /* Screen reader only — visually hidden but accessible */
    .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border-width: 0;
    }

    /* Skip-link reveal — combine with .sr-only to show on keyboard focus */
    .sr-only-focusable:focus,
    .sr-only-focusable:focus-visible {
        position: absolute;
        top: 0;
        left: 0;
        width: auto;
        height: auto;
        margin: 0;
        padding: var(--swan-sem-space-2) var(--swan-sem-space-4);
        overflow: visible;
        clip: auto;
        white-space: normal;
        background: var(--swan-sem-color-bg-standard);
        color: var(--swan-sem-color-text-standard);
        z-index: var(--swan-sem-z-index-overlay, 10);
    }

    /* Typing indicator - animated bouncing dots */
    .typing-dots {
        width: 22px;
        aspect-ratio: 2;
        --_g: no-repeat radial-gradient(circle closest-side, var(--swan-sem-color-text-standard, #000) 90%, #0000);
        background:
            var(--_g) 0%   50%,
            var(--_g) 50%  50%,
            var(--_g) 100% 50%;
        background-size: calc(100%/3) 50%;
        animation: chat-brief-typing-dots 1s infinite linear;
    }
    @keyframes chat-brief-typing-dots {
        20%  { background-position: 0%   0%, 50%  50%, 100%  50% }
        40%  { background-position: 0% 100%, 50%   0%, 100%  50% }
        60%  { background-position: 0%  50%, 50% 100%, 100%   0% }
        80%  { background-position: 0%  50%, 50%  50%, 100% 100% }
    }

    /* Edit modals - 600px on larger viewports */
    .edit-modal-600 .swan-modal-dialog {
        width: 600px;
    }

    /* Wider edit modals - 800px on lg+ viewports */
    @media (min-width: 1024px) {
        .edit-modal-800 .swan-modal-dialog {
            width: 800px;
        }
    }

    /* Sticky modal footer */
    .modal-sticky-footer .swan-modal-dialog-content {
        display: flex;
        flex-direction: column;
        max-height: 90vh;
    }
    .modal-sticky-footer .swan-modal-dialog-body {
        flex: 1;
        overflow-y: auto;
    }
    .modal-sticky-footer .swan-modal-dialog-footer {
        flex-shrink: 0;
        position: sticky;
        bottom: 0;
        background: var(--swan-sem-color-bg-standard, white);
        border-top: 1px solid var(--swan-sem-color-border-standard);
        z-index: 1;
    }

    /* Bottom bar - fixed footer matching production BottomBar */
    .bottom-bar-container {
        display: flex;
        flex-direction: column;
        background-color: var(--swan-sem-color-bg-standard, white);
        border-top: 1px solid var(--swan-sem-color-border-standard);
        box-shadow: 0 -1px 3px rgba(0, 0, 0, 0.1);
        position: fixed;
        left: 0;
        bottom: 0;
        z-index: var(--swan-sem-z-index-overlay, 10);
        width: 100%;
    }

    /* Hide scrollbar for chat messages area */
    .hide-scrollbar::-webkit-scrollbar {
        display: none;
    }

    /* Visible focus ring when navigating messages by keyboard */

    .chat-message:focus-visible {
        outline: none;
        box-shadow: var(--swan-sem-focus-tight);
        border-radius: var(--swan-sem-border-radius-standard);
    }

    /* Strip Swan TextArea border/focus when inside the chat input container */
    .chat-input-container .swan-textarea,
    .chat-input-container .swan-textarea:focus,
    .chat-input-container .swan-textarea:hover {
        box-shadow: none !important;
        outline: none !important;
        background: transparent !important;
    }

    /* Focus ring on outer chat input container */
    .chat-input-container:focus-within {
        box-shadow: inset var(--swan-sem-focus-tight) !important;
    }

    /* AI Avatar sparkle animations */
    @keyframes chat-brief-ai-avatar-sp1 {
        0% { transform: scale(1); opacity: 1; }
        35% { transform: scale(0.3); opacity: 0.2; }
        65% { transform: scale(0.3); opacity: 0.2; }
        100% { transform: scale(1); opacity: 1; }
    }
    @keyframes chat-brief-ai-avatar-sp2 {
        0% { transform: scale(0.3); opacity: 0.2; }
        15% { transform: scale(0.3); opacity: 0.2; }
        50% { transform: scale(1); opacity: 1; }
        85% { transform: scale(0.3); opacity: 0.2; }
        100% { transform: scale(0.3); opacity: 0.2; }
    }
    @keyframes chat-brief-ai-avatar-wind-down {
        from { opacity: 1; }
        to   { opacity: 0; }
    }
    .ai-avatar-sparkle-1 {
        transform-origin: 7px 12px;
        animation: chat-brief-ai-avatar-sp1 1.4s infinite ease-in-out;
    }
    .ai-avatar-sparkle-2 {
        transform-origin: 14px 4px;
        animation: chat-brief-ai-avatar-sp2 1.4s infinite ease-in-out;
    }
    .ai-avatar-sparkle-1.ai-avatar-winding-down {
        animation: chat-brief-ai-avatar-sp1 1.4s infinite ease-in-out, chat-brief-ai-avatar-wind-down 500ms ease-out forwards;
    }
    .ai-avatar-sparkle-2.ai-avatar-winding-down {
        animation: chat-brief-ai-avatar-sp2 1.4s infinite ease-in-out, chat-brief-ai-avatar-wind-down 500ms ease-out forwards;
    }

    @media (max-width: 480px) {
        .swan-selection-set-skin-tiles-mini .swan-selection-set-label {
            width: 100%;
            flex-basis: 100%;
        }
    }

    /* Respect reduced motion preference */
    @media (prefers-reduced-motion: reduce) {
        .typewriter-word {
            opacity: 1 !important;
            transition: none !important;
        }
        .ai-avatar-sparkle-1,
        .ai-avatar-sparkle-2 {
            animation: none !important;
        }
    }
`;
